import React, { useMemo, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  Link,
  Box,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import { getTLCList, createTLC } from '../../endpoints/tlc';
import { TLCCreateRequest } from '../../types/tlc';
import SPDSelector from '../../Components/SPDSelector';
import FeatureDescription from '../../Components/FeatureDescription';
import { TLC_DESCRIPTION } from '../../constants/featureDescriptions';

function apiErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: string }).message);
  }
  return 'Failed to create TLC';
}

const TLCCreate: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [state, setState] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [spdId, setSpdId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const listParams = useMemo(() => ({ page: 1, page_size: 1 }), []);

  const {
    data: listMeta,
    isLoading: metaLoading,
    isError: metaError,
  } = useApiGet(
    ['tlc-list', 'can-create-check'],
    () => getTLCList(listParams),
    { enabled: true, retry: 1 }
  );

  const { mutate: submitCreate, isPending } = useApiSend(
    (body: TLCCreateRequest) => createTLC(body),
    (res) => {
      navigate(`/tlc/${res.id}`);
    },
    (err) => {
      setFormError(apiErrorMessage(err));
    },
    [['tlc-list']]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const trimmedSpd = spdId.trim();
    if (!trimmedSpd) {
      setFormError('Please select an S/P/D.');
      return;
    }
    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }
    const payload: TLCCreateRequest = {
      name: name.trim(),
      spd_chapter_id: trimmedSpd,
      is_active: isActive,
    };
    const st = state.trim();
    if (st) {
      payload.state = st;
    }
    const n = notes.trim();
    if (n) {
      payload.notes = n;
    }
    submitCreate(payload);
  };

  const forbidden = !metaLoading && listMeta && listMeta.can_create === false;

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/tlc')}
            size="small"
          >
            Back to list
          </Button>
          <Typography variant="h4" color="primary">
            Create TLC
          </Typography>
        </Stack>
      </Grid>
      <Grid item xs={12} paddingBottom={2}>
        <FeatureDescription
          shortText={TLC_DESCRIPTION.short}
          longText={TLC_DESCRIPTION.long}
        />
      </Grid>

      {metaLoading && (
        <Grid item xs={12}>
          <Typography>Loading…</Typography>
        </Grid>
      )}

      {metaError && !metaLoading && (
        <Grid item xs={12}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load permissions.{' '}
            <Link component={RouterLink} to="/tlc">
              Return to TLC list
            </Link>
          </Alert>
        </Grid>
      )}

      {forbidden && (
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You do not have permission to create TLCs. Only users with
            international <strong>tlc:edit</strong> access can add new records.{' '}
            <Link component={RouterLink} to="/tlc">
              Return to TLC list
            </Link>
          </Alert>
        </Grid>
      )}

      {!metaLoading && !metaError && !forbidden && (
        <Grid item xs={12} md={8} lg={6}>
          <Paper sx={{ p: 3 }} component="form" onSubmit={handleSubmit}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                size="small"
                id="tlc-create-name"
              />
              <TextField
                label="State (optional)"
                value={state}
                onChange={(e) => setState(e.target.value)}
                fullWidth
                size="small"
                id="tlc-create-state"
              />
              <Box>
                <SPDSelector
                  spd={spdId}
                  handleSpdSelect={(id) => setSpdId(id || '')}
                  keyId="tlc-create"
                  all={false}
                />
              </Box>
              <TextField
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                size="small"
                id="tlc-create-notes"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    id="tlc-create-active"
                  />
                }
                label="Active"
              />
              <Stack direction="row" spacing={2}>
                <Button type="submit" variant="contained" disabled={isPending}>
                  {isPending ? 'Creating…' : 'Create TLC'}
                </Button>
                <Button variant="outlined" onClick={() => navigate('/tlc')}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default TLCCreate;
